import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Clock, Zap, Circle, ArrowLeft } from 'lucide-react';
import { chargerService } from '../services/chargerService';
import { useToastNotifications } from '../components/ToastNotification';
import { Charger, QueueDetail } from '../types';

export function PostponeQueuePage() {
  const { chargerId } = useParams<{ chargerId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addSuccessNotification, addNotification } = useToastNotifications();

  // Helper for error notifications
  const addErrorNotification = (message: string) => {
    addNotification({
      type: 'General',
      message,
      duration: 10,
      severity: 'error',
      icon: '❌'
    });
  };

  const [charger, setCharger] = useState<Charger | null>(null);
  const [queueDetails, setQueueDetails] = useState<QueueDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [queueCancelled, setQueueCancelled] = useState(false);

  // Load charger and queue details
  useEffect(() => {
    if (!chargerId) {
      addErrorNotification('postponeQueue.errorInvalidCharger');
      navigate('/');
      return;
    }

    loadChargerAndQueue();
  }, [chargerId]);

  const loadChargerAndQueue = async () => {
    try {
      setLoadingDetails(true);
      
      // Load all chargers to find the specific one
      const chargers = await chargerService.getChargers();
      const targetCharger = chargers.find(c => c.id === chargerId);
      
      if (!targetCharger) {
        addErrorNotification('postponeQueue.errorChargerNotFound');
        navigate('/');
        return;
      }

      setCharger(targetCharger);
      
      // Load queue details
      const details = await chargerService.getChargerQueue(chargerId!);
      setQueueDetails(details);
      
      // Always set loading to false first, so UI shows
      setLoadingDetails(false);
      
      // Then auto-cancel current queue position (don't block UI loading)
      if (!queueCancelled) {
        cancelCurrentQueue();
      }
      
    } catch (error) {
      console.error('Failed to load charger details:', error);
      addErrorNotification('postponeQueue.errorLoadFailed');
      navigate('/');
    }
  };

  const cancelCurrentQueue = async () => {
    if (!chargerId || queueCancelled) return;

    try {
      console.log(`🗑️ Removing user from queue using cancelQueue for charger ${chargerId}`);
      // Remove user from queue entirely so they can select a new position
      await chargerService.cancelQueue(chargerId);
      setQueueCancelled(true);
      addSuccessNotification('postponeQueue.cancelled');
      
      // Reload queue details after leaving queue to show updated state
      const details = await chargerService.getChargerQueue(chargerId);
      setQueueDetails(details);
      console.log('✅ Queue cancelled and details reloaded');
      
    } catch (error) {
      console.error('❌ Failed to cancel queue:', error);
      addErrorNotification('postponeQueue.errorCancelFailed');
    }
  };

  const handleShiftSelection = async (shift: number) => {
    console.log(`🔄 Attempting to join shift ${shift} for charger ${chargerId}`);
    console.log('Current state:', { isLoading, queueCancelled, chargerId });
    
    if (!chargerId || isLoading) {
      console.log('❌ Blocked: missing chargerId or isLoading=true');
      return;
    }

    try {
      setIsLoading(true);
      console.log(`📞 Calling chargerService.joinQueue(${chargerId}, ${shift})`);
      await chargerService.joinQueue(chargerId, shift);
      
      console.log('✅ Successfully joined queue');
      addSuccessNotification(`Successfully joined queue for shift #${shift}`);
      navigate('/');
      
    } catch (error) {
      console.error('❌ Failed to join queue:', error);
      
      let errorMessage = t('errors.generic');
      if (error instanceof Error) {
        if (error.message.includes('active charging session') || error.message.includes('הפעלת טעינה פעילה') || error.message.includes('активной сессии зарядки')) {
          errorMessage = t('errors.queueJoinActiveSession');
        } else if (error.message.includes('already in queue') || error.message.includes('כבר נמצא בתור') || error.message.includes('уже находитесь в очереди')) {
          errorMessage = t('errors.queueJoinAlreadyInQueue');
        } else if (error.message.includes('queue is full')) {
          errorMessage = 'This queue shift is full. Please try another shift.';
        }
      }
      
      addErrorNotification(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/');
  };

  if (loadingDetails) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <Clock className="animate-spin" size={48} />
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!charger || !queueDetails) {
    return (
      <div className="page-container">
        <div className="error-container">
          <p>Failed to load charger details</p>
          <button onClick={handleGoBack} className="btn-primary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="postpone-queue-page">
        {/* Header */}
        <div className="page-header">
          <button onClick={handleGoBack} className="back-button">
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1 className="page-title">{t('postponeQueue.title')}</h1>
            <p className="page-subtitle">{charger.name} - {t('home.charger.slot', { number: charger.slotNumber })}</p>
          </div>
        </div>

        {/* Queue Modal Content - Exactly same as Join Queue modal */}
        <div className="queue-modal-content">
          <div className="queue-status-section">
            <div className="queue-current-status">
              <h4>{t('dialogs.joinQueue.currentStatus')}</h4>
              {queueDetails.currentUser ? (
                <div className="current-user-info">
                  <Zap size={16} style={{ color: 'var(--color-charging)' }} />
                  <span>{t('dialogs.joinQueue.charging')}: {queueDetails.currentUser.fullName}</span>
                  {queueDetails.estimatedEndTime && (
                    <span className="estimated-end">
                      {t('dialogs.joinQueue.until')} {new Date(queueDetails.estimatedEndTime).toLocaleTimeString()}
                    </span>
                  )}
                </div>
              ) : (
                <div className="charger-idle">
                  <Zap size={16} style={{ color: 'var(--color-available)' }} />
                  <span>{t('home.charger.status.available')}</span>
                </div>
              )}
            </div>
          </div>

          <div className="shift-selection-section">
            <h4>{t('dialogs.joinQueue.selectShift')}</h4>
            <p className="shift-description">{t('dialogs.joinQueue.shiftDescription')}</p>
            
            <div className="shift-options">
              {Array.from({ length: queueDetails.maxQueueLength }, (_, index) => {
                const shiftNumber = index + 1;
                const existingUser = queueDetails.queueUsers.find(u => u.position === shiftNumber);
                const isOccupied = !!existingUser;
                const isAvailable = !isOccupied;
                
                return (
                  <button
                    key={shiftNumber}
                    className={`shift-option ${isOccupied ? 'shift-occupied' : 'shift-available'}`}
                    onClick={() => {
                      console.log(`🖱️ Shift ${shiftNumber} clicked! isAvailable=${isAvailable}, isOccupied=${isOccupied}, isLoading=${isLoading}`);
                      if (isAvailable) {
                        handleShiftSelection(shiftNumber);
                      } else {
                        console.log('❌ Click ignored - shift not available');
                      }
                    }}
                    disabled={isOccupied || isLoading}
                  >
                    <div className="shift-number">
                      <div className="shift-label">{t('dialogs.joinQueue.shift')} #{shiftNumber}</div>
                      <div className="shift-priority">
                        {shiftNumber === 1 ? t('dialogs.joinQueue.priority.highest') : 
                         shiftNumber === queueDetails.maxQueueLength ? t('dialogs.joinQueue.priority.lowest') : 
                         t('dialogs.joinQueue.priority.medium')}
                      </div>
                    </div>
                    
                    <div className="shift-details">
                      {isOccupied && existingUser ? (
                        <>
                          <div className="shift-user-name">{existingUser.fullName}</div>
                          <div className="shift-user-details">
                            <a 
                              href={`tel:${existingUser.maskedPhone.replace(/[^\d+]/g, '')}`}
                              className="phone-link inline"
                              title={`Call ${existingUser.fullName}`}
                            >
                              {existingUser.maskedPhone}
                            </a> • {existingUser.primaryPlate}
                          </div>
                          {existingUser.estimatedStartTime && (
                            <div className="shift-estimated-start">
                              {t('dialogs.joinQueue.estimatedStart')}: {new Date(existingUser.estimatedStartTime).toLocaleTimeString()}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="shift-status">{t('home.charger.status.available')}</div>
                          <div className="shift-estimate">
                            {t('dialogs.joinQueue.estimatedWait')}: ~{(shiftNumber - 1) * 60} {t('time.min')}
                          </div>
                        </>
                      )}
                    </div>
                    
                    {isAvailable && (
                      <div className="shift-select-indicator">
                        <Circle size={20} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Information message */}
          <div className="postpone-info">
            <div className="info-message">
              <Clock size={16} />
              <span>{t('postponeQueue.description')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}